import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { UserPlus, Search, Check, Phone, Mail, Star, Clock, Calendar, Users, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuickAssignModal = ({ open, onOpenChange, workOrder, onAssigned }) => {
  const { getAuthHeaders } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [bypassW9, setBypassW9] = useState(false);

  useEffect(() => {
    if (open && workOrder) {
      fetchMatchingVendors();
      setSelectedVendors([]); // Reset selection when modal opens
      setBypassW9(false); // Reset bypass toggle
    }
  }, [open, workOrder]);

  const fetchMatchingVendors = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        work_order_type: workOrder.work_order_type || 'interpreting'
      });
      
      // Add language from target_language or first language in languages array
      const lang = workOrder.target_language || workOrder.languages?.[0];
      if (lang) params.append('language', lang);
      
      // Add modality
      if (workOrder.interpreting_details?.modality) {
        params.append('modality', workOrder.interpreting_details.modality);
      }
      
      // Add service category
      if (workOrder.service_category || workOrder.interpreting_details?.service_category) {
        params.append('service_category', workOrder.service_category || workOrder.interpreting_details?.service_category);
      }

      const response = await fetch(`${API_URL}/api/vendors/match?${params}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        let vendorList = data.vendors || [];
        
        // If no matching vendors found, fetch all active vendors as fallback
        if (vendorList.length === 0) {
          const allVendorsResponse = await fetch(`${API_URL}/api/vendors?status=active`, {
            headers: getAuthHeaders(),
            credentials: 'include'
          });
          if (allVendorsResponse.ok) {
            const allVendors = await allVendorsResponse.json();
            // Mark them as non-matched
            vendorList = allVendors.map(v => ({
              ...v,
              match_score: 0,
              match_reasons: ['No specific match - showing all active vendors']
            }));
          }
        }
        
        setVendors(vendorList);
      }
    } catch (error) {
      console.error('Failed to load vendors:', error);
      toast.error('Failed to load matching vendors');
    } finally {
      setLoading(false);
    }
  };

  const toggleVendorSelection = (vendor) => {
    setSelectedVendors(prev => {
      const isSelected = prev.some(v => v.vendor_id === vendor.vendor_id);
      if (isSelected) {
        return prev.filter(v => v.vendor_id !== vendor.vendor_id);
      } else {
        return [...prev, vendor];
      }
    });
  };

  const handleAssign = async () => {
    if (selectedVendors.length === 0) return;
    
    setAssigning(true);
    try {
      let successCount = 0;
      let failedVendors = [];
      let bypassedVendors = [];
      
      // Create assignments for each selected vendor
      for (const vendor of selectedVendors) {
        // Check if vendor is missing W-9
        const vendorMissingW9 = !vendor.w9_on_file;
        
        // Build URL with bypass parameter if needed
        const url = bypassW9 && vendorMissingW9 
          ? `${API_URL}/api/assignments?bypass_w9=true`
          : `${API_URL}/api/assignments`;
        
        const response = await fetch(url, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            work_order_id: workOrder.work_order_id,
            vendor_id: vendor.vendor_id,
            role: 'interpreter'
          })
        });
        
        if (response.ok) {
          successCount++;
          if (bypassW9 && vendorMissingW9) {
            const vendorName = vendor.first_name ? `${vendor.first_name} ${vendor.last_name || ''}`.trim() : vendor.name;
            bypassedVendors.push(vendorName);
          }
        } else {
          // Get the specific error message from the backend
          const errorData = await response.json().catch(() => ({}));
          const vendorName = vendor.first_name ? `${vendor.first_name} ${vendor.last_name || ''}`.trim() : vendor.name;
          const reason = errorData.detail || 'Unknown error';
          failedVendors.push({ name: vendorName, reason });
        }
      }
      
      if (successCount > 0) {
        const vendorNames = selectedVendors.slice(0, successCount).map(v => v.first_name || v.name?.split(' ')[0]).join(', ');
        toast.success(`${successCount} vendor${successCount > 1 ? 's' : ''} assigned successfully: ${vendorNames}`);
        
        // Show warning for bypassed W-9
        if (bypassedVendors.length > 0) {
          toast.warning(`⚠️ W-9 missing for: ${bypassedVendors.join(', ')}. Please collect W-9 forms!`, {
            duration: 10000
          });
        }
        
        onOpenChange(false);
        if (onAssigned) onAssigned();
      }
      
      // Show specific error messages for each failed vendor
      if (failedVendors.length > 0) {
        failedVendors.forEach(({ name, reason }) => {
          toast.error(`Cannot assign ${name}: ${reason}`, {
            duration: 8000,
            description: 'Please update vendor profile to resolve this issue'
          });
        });
      }
    } catch (error) {
      toast.error('Failed to assign vendors');
    } finally {
      setAssigning(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'TBD';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return 'TBD';
    }
  };

  const filteredVendors = vendors.filter(v => {
    const searchLower = search.toLowerCase();
    return (
      v.first_name?.toLowerCase().includes(searchLower) ||
      v.last_name?.toLowerCase().includes(searchLower) ||
      v.business_name?.toLowerCase().includes(searchLower) ||
      v.name?.toLowerCase().includes(searchLower) ||
      v.email?.toLowerCase().includes(searchLower) ||
      v.vendor_number?.toLowerCase().includes(searchLower)
    );
  });

  const getModalityLabel = (modality) => {
    const labels = {
      'onsite': 'On-Site',
      'opi': 'OPI',
      'vri': 'VRI'
    };
    return labels[modality] || modality;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-blue-600" />
            Assign Interpreter{workOrder?.interpreting_details?.team_required ? 's (Team)' : ''}
          </DialogTitle>
          {selectedVendors.length > 0 && (
            <div className="flex items-center gap-2 mt-2">
              <Badge className="bg-blue-100 text-blue-800">
                <Users className="h-3 w-3 mr-1" />
                {selectedVendors.length} selected
              </Badge>
              <span className="text-xs text-muted-foreground">
                {selectedVendors.map(v => v.first_name || v.name?.split(' ')[0]).join(', ')}
              </span>
            </div>
          )}
        </DialogHeader>

        {/* Work Order Summary */}
        {workOrder && (
          <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono font-semibold text-blue-800">{workOrder.work_order_id}</span>
              <Badge variant="outline" className="border-blue-300 text-blue-700">
                {workOrder.status?.replace(/_/g, ' ')}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-1 text-gray-600">
                <Calendar className="h-3 w-3" />
                <span>{formatDateTime(workOrder.interpreting_details?.start_datetime)}</span>
              </div>
              <div className="text-gray-600">
                <span className="font-medium">Client:</span> {workOrder.client_name}
              </div>
              <div className="text-gray-600">
                <span className="font-medium">Language:</span> {(workOrder.target_language || workOrder.languages?.[0] || 'N/A').toUpperCase()}
              </div>
              <div className="text-gray-600">
                <span className="font-medium">Setting:</span> {getModalityLabel(workOrder.interpreting_details?.modality)}
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search vendors by name or ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="vendor-search"
          />
        </div>

        {/* Vendor List */}
        <div className="flex-1 overflow-y-auto border rounded-lg">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredVendors.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <UserPlus className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No matching vendors found</p>
              <p className="text-sm mt-1">Try adjusting your search or work order details</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredVendors.map((vendor) => {
                const isSelected = selectedVendors.some(v => v.vendor_id === vendor.vendor_id);
                return (
                <div
                  key={vendor.vendor_id}
                  className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-blue-100 border-l-4 border-blue-600'
                      : vendor.is_recommended 
                        ? 'bg-green-50/50 hover:bg-green-100/50'
                        : 'hover:bg-gray-50'
                  }`}
                  onClick={() => toggleVendorSelection(vendor)}
                  data-testid={`vendor-option-${vendor.vendor_id}`}
                >
                  {/* Selection Checkbox */}
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleVendorSelection(vendor)}
                    className="flex-shrink-0"
                  />

                  {/* Vendor Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold truncate">
                        {vendor.last_name}, {vendor.first_name}
                        {vendor.business_name && <span className="font-normal text-muted-foreground ml-1">({vendor.business_name})</span>}
                      </span>
                      {vendor.vendor_number && (
                        <span className="text-xs text-muted-foreground font-mono">{vendor.vendor_number}</span>
                      )}
                      {vendor.is_recommended && (
                        <Badge className="bg-green-100 text-green-800 text-xs">
                          <Star className="h-3 w-3 mr-1" />
                          Recommended
                        </Badge>
                      )}
                      {!vendor.w9_on_file && (
                        <Badge variant="outline" className="border-amber-400 text-amber-600 text-xs">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          No W-9
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                      {vendor.email && (
                        <span className="flex items-center gap-1 truncate">
                          <Mail className="h-3 w-3" />
                          {vendor.email}
                        </span>
                      )}
                      {vendor.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {vendor.phone}
                        </span>
                      )}
                    </div>
                    {vendor.match_reasons?.length > 0 && (
                      <p className="text-xs text-blue-600 mt-1">
                        {vendor.match_reasons.slice(0, 3).join(' • ')}
                      </p>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="text-right text-sm flex-shrink-0">
                    <p className="font-semibold text-blue-600">{vendor.match_score || 0} pts</p>
                    <p className="text-green-600 flex items-center justify-end gap-1">
                      <Star className="h-3 w-3" />
                      {vendor.reliability_score || 100}%
                    </p>
                    <p className="text-muted-foreground flex items-center justify-end gap-1">
                      <Clock className="h-3 w-3" />
                      {vendor.total_assignments || 0} jobs
                    </p>
                  </div>
                </div>
              );})}
            </div>
          )}
        </div>

        {/* W-9 Bypass Option */}
        <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg mt-4">
          <Checkbox
            id="bypass-w9"
            checked={bypassW9}
            onCheckedChange={setBypassW9}
          />
          <label htmlFor="bypass-w9" className="text-sm cursor-pointer">
            <span className="font-medium text-amber-800">Allow assignment without W-9</span>
            <span className="block text-xs text-amber-600">
              Vendor will still be reminded. Assignment will appear in nightly compliance report.
            </span>
          </label>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={selectedVendors.length === 0 || assigning}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="confirm-assign-btn"
          >
            {assigning ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Assigning...
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4 mr-2" />
                Assign {selectedVendors.length > 0 ? `${selectedVendors.length} Vendor${selectedVendors.length > 1 ? 's' : ''}` : 'Vendor'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickAssignModal;
